import React, { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/DashboardBits";
import ExecFormDialog from "@/components/common/ExecFormDialog";
import { Card, CardContent } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Dialog, DialogTrigger } from "@/components/ui/dialog";
import { toast } from "sonner";
import { X, UserMinus, UserPlus, Plus } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ManagerExecutives() {
  const { user } = useAuth();
  const myId = user?.entity_id;
  const [list, setList] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [addOpen, setAddOpen] = useState(false);

  const load = () => api.get("/executives").then((r) => setList(r.data));
  useEffect(() => { load(); }, []);

  const setApproval = async (id, approve) => {
    setBusyId(id);
    try {
      await api.patch(`/executives/${id}/approval`, { approve });
      toast.success(approve ? "Added to your team" : "Removed from your team");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusyId(null);
    }
  };

  const confirmRemove = (e) => {
    if (window.confirm(`Remove ${e.name} from your team? They will need to be re-approved to work on leads.`)) {
      setApproval(e.id, false);
    }
  };

  const pending = list.filter((e) => !e.is_approved);
  const approved = list.filter((e) => e.is_approved);
  const myTeam = approved.filter((e) => e.manager_id === myId);
  const others = approved.filter((e) => e.manager_id !== myId);

  return (
    <div>
      <PageHeader
        title="My Executives"
        subtitle={`${pending.length} pending · ${myTeam.length} on my team · ${others.length} in territory`}
        right={
          <Dialog open={addOpen} onOpenChange={setAddOpen}>
            <DialogTrigger asChild>
              <Button className="bg-slate-950 hover:bg-slate-800 text-white" data-testid="manager-add-exec-button">
                <Plus className="h-4 w-4 mr-1" /> Add Executive
              </Button>
            </DialogTrigger>
            <ExecFormDialog
              showManagerPicker={false}
              onClose={() => setAddOpen(false)}
              onSaved={() => { setAddOpen(false); load(); toast.success("Executive added to your team"); }}
            />
          </Dialog>
        }
      />

      {/* PENDING */}
      {pending.length > 0 && (
        <div className="mb-6" data-testid="pending-section">
          <div className="text-xs font-bold uppercase tracking-widest text-red-500 mb-2">Pending approvals</div>
          <Card className="border border-red-200 bg-red-50/40">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Pincode</TableHead>
                    <TableHead>Working Location</TableHead>
                    <TableHead className="text-right">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {pending.map((e) => (
                    <TableRow key={e.id} data-testid={`pending-exec-${e.id}`}>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {e.email}<br />{e.phone}
                      </TableCell>
                      <TableCell className="num">{e.pincode}</TableCell>
                      <TableCell>{e.city ? `${e.city}, ${e.working_location}` : e.working_location}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          onClick={() => setApproval(e.id, true)}
                          disabled={busyId === e.id}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white mr-2"
                          data-testid={`approve-exec-${e.id}`}
                        >
                          <UserPlus className="h-4 w-4 mr-1" /> Add to team
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => setApproval(e.id, false)}
                          disabled={busyId === e.id}
                          className="text-red-600"
                          data-testid={`reject-exec-${e.id}`}
                        >
                          <X className="h-4 w-4 mr-1" /> Reject
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </div>
      )}

      {/* MY TEAM */}
      <div className="text-xs font-bold uppercase tracking-widest text-emerald-700 mb-2">
        My team
      </div>
      <Card className="border border-slate-200 mb-6" data-testid="my-team-section">
        <CardContent className="p-0 overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Contact</TableHead>
                <TableHead>Pincode</TableHead>
                <TableHead>Working Location</TableHead>
                <TableHead>Radius</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {myTeam.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-slate-500 py-8">
                    No executives on your team yet. Approve someone from the pending list to add them.
                  </TableCell>
                </TableRow>
              )}
              {myTeam.map((e) => (
                <TableRow key={e.id} data-testid={`myteam-exec-${e.id}`}>
                  <TableCell className="font-medium">{e.name}</TableCell>
                  <TableCell className="text-xs text-slate-600">
                    {e.email}<br />{e.phone}
                  </TableCell>
                  <TableCell className="num">{e.pincode}</TableCell>
                  <TableCell>{e.city ? `${e.city}, ${e.working_location}` : e.working_location}</TableCell>
                  <TableCell className="num text-slate-600">{e.working_radius_km} km</TableCell>
                  <TableCell className="text-right">
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 hover:bg-red-50"
                      onClick={() => confirmRemove(e)}
                      disabled={busyId === e.id}
                      data-testid={`remove-exec-${e.id}`}
                    >
                      <UserMinus className="h-4 w-4 mr-1" /> Remove
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* OTHERS IN TERRITORY (approved by another manager or admin) */}
      {others.length > 0 && (
        <>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
            Others in territory
          </div>
          <Card className="border border-slate-200" data-testid="others-section">
            <CardContent className="p-0 overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Pincode</TableHead>
                    <TableHead>Working Location</TableHead>
                    <TableHead>Radius</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {others.map((e) => (
                    <TableRow key={e.id} data-testid={`other-exec-${e.id}`}>
                      <TableCell className="font-medium">{e.name}</TableCell>
                      <TableCell className="text-xs text-slate-600">
                        {e.email}<br />{e.phone}
                      </TableCell>
                      <TableCell className="num">{e.pincode}</TableCell>
                      <TableCell>{e.city ? `${e.city}, ${e.working_location}` : e.working_location}</TableCell>
                      <TableCell className="num text-slate-600">{e.working_radius_km} km</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
